/** **Export…** in the page header (spec §8.20): every reader may export; the archive is built on the server. */
import { Download } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { SyncStatus } from "../sync/docManager";
import { downloadUrl, exportPage } from "./api";
import { useT } from "../i18n";

const SYNC_WAIT_MS = 10_000;

export function ExportPageButton({ docId, status }: { docId: string; status: SyncStatus }) {
  const t = useT();
  const [phase, setPhase] = useState<"idle" | "syncing" | "building" | "done" | "failed">("idle");
  const [message, setMessage] = useState<string | null>(null);
  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const run = async () => {
    setMessage(null);
    // the archive is built from the server's copy: give local edits a moment to arrive
    setPhase("syncing");
    const deadline = Date.now() + SYNC_WAIT_MS;
    while (statusRef.current !== "synced" && Date.now() < deadline) await new Promise((r) => setTimeout(r, 200));
    const unsynced = statusRef.current !== "synced";
    setPhase("building");
    try {
      const job = await exportPage(docId);
      const a = document.createElement("a");
      a.href = downloadUrl(job);
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      const notes = ((job.result?.notes as string[] | undefined) ?? []).length;
      setMessage([unsynced && t("export.unsynced"), notes > 0 && t("export.notes", { count: notes })].filter(Boolean).join(" ") || null);
      setPhase("done");
    } catch (e) {
      setMessage(e instanceof Error ? e.message.replace(/^\d+: /, "") : String(e));
      setPhase("failed");
    }
  };

  const busy = phase === "syncing" || phase === "building";
  return (
    <span className="flex items-center gap-1 text-xs" data-testid="export-page" data-phase={phase}>
      <button
        type="button"
        className="flex items-center gap-1 rounded px-2 py-0.5 hover:bg-neutral-200 disabled:opacity-50 dark:hover:bg-neutral-800"
        title={navigator.onLine ? t("export.title") : t("export.offline")}
        disabled={busy || !navigator.onLine}
        onClick={() => void run()}
        data-testid="export-page-button"
      >
        <Download size={14} /> {busy ? t("export.preparing") : t("export.button")}
      </button>
      {message && (
        <span className={phase === "failed" ? "text-red-700" : "text-neutral-500"} data-testid="export-page-message">
          {message}
        </span>
      )}
    </span>
  );
}
